import Pica from 'pica';

const pica = Pica({ features: ['js', 'wasm', 'ww', 'cib'] });

function getOrientation(exifData) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onerror = () => {
            reader.abort();
            return reject(-3);
        };

        reader.onload = (event) => {
            const view = new DataView(event.target.result);

            if (view.getUint16(0, false) != 0xFFD8) {
                // this could either be no image file, or an image file with no EXIF data, like a PNG file
                return resolve(1);
            }

            const length = view.byteLength;
            let offset = 2;

            while (offset < length) {
                const marker = view.getUint16(offset, false);
                offset += 2;

                if (marker == 0xFFE1) {
                    if (view.getUint32(offset += 2, false) != 0x45786966) {
                        return resolve(1);
                    }
                    const little = view.getUint16(offset += 6, false) == 0x4949;
                    offset += view.getUint32(offset + 4, little);
                    const tags = view.getUint16(offset, little);
                    offset += 2;

                    for (let i = 0; i < tags; i++) if (view.getUint16(offset + (i * 12), little) == 0x0112) return resolve(view.getUint16(offset + (i * 12) + 8, little));
                } else if ((marker & 0xFF00) != 0xFF00) {
                    break;
                } else {
                    offset += view.getUint16(offset, false);
                }
            }
            return resolve(1);
        };

        reader.readAsArrayBuffer(exifData);
    });
}

function calculateSizeToFitMaxSize(img, maxSize) {
    let width;
    let height;
    if (img.width > img.height) {
        width = Math.min(img.width, maxSize);
        height = parseInt(img.height * width / img.width, 10);
    } else {
        height = Math.min(img.height, maxSize);
        width = parseInt(img.width * height / img.height, 10);
    }
    return {
        width,
        height,
    };
}

export default {
    read(file, maxSize = 1920, maxThumbnailSize = 480) {
        return new Promise((resolve, reject) => {
            getOrientation(file.slice(0, 64 * 1024))
                .catch((errorCode) => {
                    switch (errorCode) {
                        case -1:
                            reject('Error while determining photo orientation: offset < length');
                            break;
                        case -3:
                            reject('Error while determining photo orientation: FileReader failed to read');
                            break;
                        default:
                            reject('Error while determining photo orientation: unknown error');
                            break;
                    }
                })
                .then((orientation) => {
                    const img = new Image();
                    const url = URL.createObjectURL(file);

                    img.onerror = () => {
                        reject('Error while resetting orientation: image loading failed');
                    };

                    img.onload = () => {
                        URL.revokeObjectURL(url);

                        const { width, height } = calculateSizeToFitMaxSize(img, maxSize);
                        const { width: thumbnailWidth, height: thumbnailHeight } = calculateSizeToFitMaxSize(img, maxThumbnailSize);

                        let canvas = document.createElement('canvas');
                        let rotatedCanvas = document.createElement('canvas');
                        let thumbnailCanvas = document.createElement('canvas');

                        canvas.width = width;
                        canvas.height = height;

                        if (orientation > 4 && orientation < 9) {
                            rotatedCanvas.width = height;
                            rotatedCanvas.height = width;
                            thumbnailCanvas.width = thumbnailHeight;
                            thumbnailCanvas.height = thumbnailWidth;
                        } else {
                            rotatedCanvas.width = width;
                            rotatedCanvas.height = height;
                            thumbnailCanvas.width = thumbnailWidth;
                            thumbnailCanvas.height = thumbnailHeight;
                        }

                        let rotatedCtx = rotatedCanvas.getContext('2d');

                        switch (orientation) {
                            case 2:
                                rotatedCtx.transform(-1, 0, 0, 1, width, 0);
                                break;
                            case 3:
                                rotatedCtx.transform(-1, 0, 0, -1, width, height);
                                break;
                            case 4:
                                rotatedCtx.transform(1, 0, 0, -1, 0, height);
                                break;
                            case 5:
                                rotatedCtx.transform(0, 1, 1, 0, 0, 0);
                                break;
                            case 6:
                                rotatedCtx.transform(0, 1, -1, 0, height, 0);
                                break;
                            case 7:
                                rotatedCtx.transform(0, -1, -1, 0, height, width);
                                break;
                            case 8:
                                rotatedCtx.transform(0, -1, 1, 0, 0, width);
                                break;
                            default:
                                break;
                        }

                        pica
                            .resize(img, canvas)
                            .then(() => {
                                rotatedCtx.drawImage(canvas, 0, 0);
                                return pica.resize(rotatedCanvas, thumbnailCanvas);
                            })
                            .then(() => resolve({
                                photo: rotatedCanvas.toDataURL('image/jpeg', 0.7),
                                thumbnail: thumbnailCanvas.toDataURL('image/jpeg', 0.5),
                                width: rotatedCanvas.width,
                                height: rotatedCanvas.height,
                            }))
                            .finally(() => {
                                // cleanup
                                canvas.width = canvas.height = 0;
                                rotatedCanvas.width = rotatedCanvas.height = 0;
                                thumbnailCanvas.width = thumbnailCanvas.height = 0;
                                canvas = rotatedCtx = rotatedCanvas = thumbnailCanvas = null;
                            });
                    };

                    img.src = url;
                });
        });
    },
};
